/**
 * Modal de confirmation réutilisable
 * Remplace les window.confirm() et alert() natifs
 */

import { AlertTriangle, CheckCircle, Info, XCircle } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "../ui/button";

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info" | "success";
  children?: React.ReactNode;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Confirmer",
  cancelText = "Annuler",
  variant = "warning",
  children,
}: ConfirmModalProps) {
  const variantStyles = {
    danger: {
      icon: XCircle,
      iconBg: "bg-[var(--color-brick-bg)]",
      iconColor: "text-[var(--color-brick)]",
      buttonBg: "bg-[var(--color-brick)] hover:bg-[var(--color-brick-light)]",
    },
    warning: {
      icon: AlertTriangle,
      iconBg: "bg-[var(--color-amber-bg)]",
      iconColor: "text-[var(--color-amber)]",
      buttonBg: "bg-[var(--color-amber)] hover:bg-[var(--color-amber-light)]",
    },
    info: {
      icon: Info,
      iconBg: "bg-[var(--color-steel-bg)]",
      iconColor: "text-[var(--color-steel)]",
      buttonBg: "bg-[var(--color-steel)] hover:bg-[var(--color-steel-light)]",
    },
    success: {
      icon: CheckCircle,
      iconBg: "bg-[var(--color-olive-bg)]",
      iconColor: "text-[var(--color-olive)]",
      buttonBg: "bg-[var(--color-olive)] hover:bg-[var(--color-olive-light)]",
    },
  };

  const style = variantStyles[variant];
  const Icon = style.icon;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in" />
        <Dialog.Content className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] bg-[var(--card)] rounded-lg shadow-2xl border border-[var(--border)] max-w-md w-[90vw] z-50 animate-in fade-in zoom-in-95">
          <div className="p-6">
            {/* Header avec icône */}
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-12 h-12 rounded-full ${style.iconBg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-6 h-6 ${style.iconColor}`} />
              </div>
              <div className="flex-1">
                <Dialog.Title className="text-lg font-bold text-[var(--foreground)] mb-1">
                  {title}
                </Dialog.Title>
                <Dialog.Description className="text-sm text-[var(--muted-foreground)]">
                  {description}
                </Dialog.Description>
              </div>
            </div>

            {/* Contenu additionnel */}
            {children && (
              <div className="mb-6">
                {children}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={onClose}
                className="flex-1"
              >
                {cancelText}
              </Button>
              <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium ${style.buttonBg}`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}